import React, { useState } from 'react';
import {
    Box, VStack, HStack, Text, Input, Button, Spinner, useToast, Flex,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

// Format +1XXXXXXXXXX as (XXX) XXX-XXXX for display; fall back to raw.
function formatPhone(raw) {
    const d = String(raw || '').replace(/\D/g, '');
    const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
    if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
    return raw || '';
}

/**
 * Platform-admin only: search customers and change a customer's phone number
 * (the OTP login key). The backend normalizes to +1XXXXXXXXXX and rejects a
 * number already used by another customer.
 */
export default function CustomerPhoneSection({ platformKey, onAuthError }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [phoneDraft, setPhoneDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    const headers = { 'x-platform-key': platformKey };

    const handleAuthErr = (err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
            onAuthError && onAuthError();
            return true;
        }
        return false;
    };

    const search = async () => {
        setSearching(true);
        try {
            const res = await axios.get(`${API_URL}/api/platform/customer-search`, {
                params: { q: query },
                headers,
            });
            setResults(res.data?.customers || []);
        } catch (err) {
            if (!handleAuthErr(err)) {
                toast({ title: 'Search failed', status: 'error', duration: 3000 });
            }
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const savePhone = async (customerId) => {
        const digits = String(phoneDraft || '').replace(/\D/g, '');
        const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
        if (ten.length !== 10) {
            toast({ title: 'Enter a valid 10-digit US phone number', status: 'warning', duration: 3000 });
            return;
        }
        setSaving(true);
        try {
            const res = await axios.patch(
                `${API_URL}/api/platform/customer-phone`,
                { customerId, phoneNumber: phoneDraft },
                { headers },
            );
            const newPhone = res.data?.phoneNumber;
            setResults((prev) => prev.map((c) =>
                c.customerId === customerId ? { ...c, phoneNumber: newPhone || `+1${ten}` } : c
            ));
            toast({ title: 'Phone updated', status: 'success', duration: 2500 });
            setEditingId(null);
        } catch (err) {
            if (!handleAuthErr(err)) {
                const msg = err?.response?.status === 409
                    ? 'Another customer already uses this phone number'
                    : (err?.response?.data?.detail || 'Update failed');
                toast({ title: 'Could not update phone', description: msg, status: 'error', duration: 4000 });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <VStack spacing={4} align="stretch">
            <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                    Change a customer's phone number (their login). Search by name or phone.
                </Text>
                <HStack>
                    <Input
                        placeholder="Search by name or phone"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                    />
                    <Button colorScheme="blue" onClick={search} isLoading={searching} loadingText="Searching">
                        Search
                    </Button>
                </HStack>
            </Box>

            {searching ? (
                <Flex justify="center" py={6}><Spinner /></Flex>
            ) : results.length === 0 ? (
                <Text fontSize="sm" color="gray.400">
                    {query ? 'No matching customers.' : 'Search to find a customer.'}
                </Text>
            ) : (
                <VStack spacing={2} align="stretch">
                    {results.map((c) => (
                        <Box key={c.customerId} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                                <Box>
                                    <Text fontWeight="600">{c.name}</Text>
                                    <Text fontSize="sm" color="gray.600">
                                        {formatPhone(c.phoneNumber)}{c.email ? ` \u00b7 ${c.email}` : ''}
                                    </Text>
                                </Box>
                                {editingId === c.customerId ? (
                                    <HStack>
                                        <Input
                                            size="sm"
                                            w="180px"
                                            placeholder="New phone (10 digits)"
                                            value={phoneDraft}
                                            onChange={(e) => setPhoneDraft(e.target.value)}
                                        />
                                        <Button size="sm" colorScheme="blue" isLoading={saving}
                                            onClick={() => savePhone(c.customerId)}>
                                            Save
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                            Cancel
                                        </Button>
                                    </HStack>
                                ) : (
                                    <Button size="sm" variant="outline"
                                        onClick={() => { setEditingId(c.customerId); setPhoneDraft(c.phoneNumber || ''); }}>
                                        Change Phone
                                    </Button>
                                )}
                            </Flex>
                        </Box>
                    ))}
                </VStack>
            )}
        </VStack>
    );
}
